import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { httpService } from '@/api/v1/http.service'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import type { PaginationParams, SortingParams } from '@/types/params'
import type { DeepCamelCase } from '@/types/utils'
import { idAndNameSchema, requiredString } from '@/utils/zod'

import {
  SECTION_STATUSES,
  type SectionStatus
} from '../constants/section-status'

type APIResponse = z.infer<typeof responseSchema>
export type SectionsList = DeepCamelCase<APIResponse>
export type SectionsListItem = SectionsList[number]

const responseSchema = z.array(
  z.object({
    id: requiredString(),
    name: requiredString(),
    course: z.object({ display_name: requiredString(), id: requiredString() }),
    curriculum: idAndNameSchema,
    semesters: z.array(z.number()),
    students_count: z.number(),
    status: z.enum(SECTION_STATUSES),
    can_be_deleted: z.boolean(),
    can_be_edited: z.boolean()
  })
)

const parseResponse = (response: APIResponse): SectionsList =>
  response.map(item => ({
    id: item.id,
    name: item.name,
    course: {
      id: item.course.id,
      displayName: item.course.display_name
    },
    curriculum: item.curriculum,
    semesters: item.semesters,
    studentsCount: item.students_count,
    status: item.status,
    canBeDeleted: item.can_be_deleted,
    canBeEdited: item.can_be_edited
  }))

type Filters = {
  ids?: string[]
  courseIds?: string[]
  curriculumIds?: string[]
  statuses?: SectionStatus[]
}

type Params = Filters & PaginationParams & SortingParams

export const useSections = (params: Params = {}) =>
  useQuery({
    queryKey: ['panelSectionsList', params],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const response = await httpService.panel.panelSectionsList({
        id: params.ids,
        course_id: params.courseIds,
        curriculum_id: params.curriculumIds,
        status: params.statuses,
        page: params.page ?? 1,
        page_size: params.pageSize ?? DEFAULT_PAGE_SIZE,
        fetchKeys: {
          id: true,
          name: true,
          course: true,
          curriculum: true,
          semesters: true,
          students_count: true,
          status: true,
          can_be_deleted: true,
          can_be_edited: true
        }
      })

      const parsed = parseResponse(responseSchema.parse(response.results))
      return { list: parsed, count: response.count }
    }
  })
